import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1600";
const CONSULTING_IMG =
  "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1400";
const MARKETING_IMG =
  "https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1400";
const AI_IMG =
  "https://images.pexels.com/photos/8438969/pexels-photo-8438969.jpeg?auto=compress&cs=tinysrgb&w=1400";

const services = [
  { n: "I", title: "Business Consulting", d: "Strategy, operations, and management consulting for leaders ready to move." },
  { n: "II", title: "AI Strategy, Training & Consulting", d: "Turn AI into leverage — with a roadmap, training, and pilots that prove ROI.", featured: true },
  { n: "III", title: "Marketing & Branding", d: "Positioning, identity, and campaigns that make the market lean in." },
  { n: "IV", title: "Custom Solutions", d: "Tailored engagements — from workshops to fractional leadership." },
];

export default function LuxeHome() {
  return (
    <div data-testid="luxe-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7 relative">
            <div className="font-fraunces italic text-[#b8964f] text-lg md:text-xl mb-6">
              <span className="gold-rule"></span>MKA Business Solutions · Indianapolis
            </div>
            <h1 className="luxe-hero font-serif-display text-[#0a1f5c] font-medium">
              Strategy with{" "}
              <em className="font-serif-editorial italic font-normal text-[#b8964f]">
                soul.
              </em>
              <br />
              Growth with{" "}
              <em className="font-serif-editorial italic font-normal text-[#b8964f]">
                intention.
              </em>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl font-fraunces text-[#3a3a37] leading-relaxed">
              We partner with ambitious founders and mid-market leaders to
              clarify strategy, sharpen brand, and build the operating systems
              that turn plans into legacy.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="luxe-hero-cta"
                className="inline-flex items-center gap-3 bg-[#0a1f5c] text-[#f7f2ea] px-8 py-4 rounded-full font-fraunces text-base tracking-wide hover:bg-[#b8964f] hover:text-[#0a1f5c] transition-all"
              >
                Request a Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="luxe-hero-services"
                className="font-serif-display italic text-[#0a1f5c] text-lg luxe-underline"
              >
                Explore our practice
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="gold-frame relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="Diverse leadership team"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="absolute -bottom-6 -left-4 md:-bottom-8 md:-left-8 bg-[#f7f2ea] border border-[#e6dbc7] px-5 py-4 shadow-sm">
              <div className="font-fraunces italic text-[#b8964f] text-xs tracking-widest uppercase">
                Est. 2024
              </div>
              <div className="font-serif-display text-[#0a1f5c] text-2xl mt-1">
                Indianapolis
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="border-y border-[#e6dbc7] bg-[#f0e8d8]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase">
              I. Philosophy
            </div>
          </div>
          <div className="md:col-span-8">
            <p className="font-serif-editorial text-3xl md:text-5xl leading-[1.15] text-[#0a1f5c] font-light">
              We believe the best strategy is one that gets{" "}
              <em className="italic text-[#b8964f]">executed</em> — and the
              boldest brand is one that's honest enough to be{" "}
              <em className="italic text-[#b8964f]">remembered</em>.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES — editorial list */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-4">
              II. Practices
            </div>
            <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-[#0a1f5c]">
              Four disciplines,
              <br />
              <em className="italic text-[#b8964f]">one</em> outcome.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="luxe-see-services"
            className="font-serif-display italic text-lg text-[#0a1f5c] luxe-underline"
          >
            See all services →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {/* Service 01 - image + text */}
          <article
            data-testid="luxe-service-consulting"
            className="group"
          >
            <div className="aspect-[4/3] overflow-hidden mb-6 bg-[#f0e8d8]">
              <img
                src={CONSULTING_IMG}
                alt="Business consulting"
                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
            </div>
            <div className="font-fraunces italic text-[#b8964f] text-sm">I.</div>
            <h3 className="mt-2 font-serif-display text-3xl md:text-4xl text-[#0a1f5c]">
              Business Consulting
            </h3>
            <p className="mt-3 font-fraunces text-[#3a3a37] text-lg leading-relaxed">
              Strategy, operations, and management consulting for leadership
              teams who refuse to guess.
            </p>
          </article>

          {/* Service 02 - AI featured */}
          <article
            data-testid="luxe-service-ai"
            className="bg-[#0a1f5c] text-[#f7f2ea] p-10 md:p-14 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#b8964f]/10 rounded-full -translate-y-16 translate-x-16" />
            <div className="font-fraunces italic text-[#d4b478] text-sm">II.  · New</div>
            <h3 className="mt-2 font-serif-display text-3xl md:text-4xl text-[#f7f2ea]">
              AI Strategy, Training <em className="italic text-[#d4b478]">&</em> Consulting
            </h3>
            <p className="mt-4 font-fraunces text-[#f0e8d8]/85 text-lg leading-relaxed">
              Cut through the hype. Identify where AI creates real leverage —
              then train your team, build the workflows, and ship the pilots
              that prove ROI.
            </p>
            <div className="mt-6 aspect-[16/10] overflow-hidden">
              <img
                src={AI_IMG}
                alt="AI strategy"
                className="w-full h-full object-cover"
              />
            </div>
          </article>

          {/* Service 03 */}
          <article data-testid="luxe-service-marketing" className="group">
            <div className="aspect-[4/3] overflow-hidden mb-6 bg-[#f0e8d8]">
              <img
                src={MARKETING_IMG}
                alt="Marketing & branding"
                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
            </div>
            <div className="font-fraunces italic text-[#b8964f] text-sm">III.</div>
            <h3 className="mt-2 font-serif-display text-3xl md:text-4xl text-[#0a1f5c]">
              Marketing <em className="italic text-[#b8964f]">&</em> Branding
            </h3>
            <p className="mt-3 font-fraunces text-[#3a3a37] text-lg leading-relaxed">
              Positioning, identity systems, and campaigns that turn attention
              into revenue.
            </p>
          </article>

          {/* Service 04 - editorial */}
          <article
            data-testid="luxe-service-custom"
            className="border border-[#e6dbc7] p-10 md:p-14 bg-[#fbf8f3] flex flex-col justify-between"
          >
            <div>
              <div className="font-fraunces italic text-[#b8964f] text-sm">IV.</div>
              <h3 className="mt-2 font-serif-display text-3xl md:text-4xl text-[#0a1f5c]">
                Custom Solutions
              </h3>
              <p className="mt-4 font-fraunces text-[#3a3a37] text-lg leading-relaxed">
                When your challenge doesn't fit a box, neither does our
                engagement. Workshops, fractional leadership, retained
                advisory — shaped to the moment.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="luxe-custom-cta"
              className="mt-8 self-start inline-flex items-center gap-3 border border-[#0a1f5c] text-[#0a1f5c] px-6 py-3 rounded-full font-fraunces text-sm hover:bg-[#0a1f5c] hover:text-[#f7f2ea] transition-all"
            >
              Discuss a custom brief <ArrowRight className="w-4 h-4" />
            </Link>
          </article>
        </div>
      </section>

      {/* METRICS */}
      <section className="bg-[#0a1f5c] text-[#f7f2ea]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center mb-14">
            <div className="font-fraunces italic text-[#d4b478] text-sm tracking-widest uppercase">
              III. By the numbers
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-6">
            {[
              { k: "1:1", v: "Founder-led engagements" },
              { k: "IV", v: "Core practices" },
              { k: "Nationwide", v: "Reach from Indianapolis" },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="font-serif-display text-6xl md:text-7xl lg:text-8xl text-[#d4b478] font-medium">
                  {m.k}
                </div>
                <div className="mt-2 font-fraunces italic text-[#f0e8d8]/80 text-base tracking-wide">
                  {m.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - editorial pull quote */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-2">
            <Quote className="w-14 h-14 text-[#b8964f]" />
          </div>
          <div className="md:col-span-10">
            <blockquote
              data-testid="luxe-testimonial"
              className="font-serif-editorial italic text-3xl md:text-5xl leading-[1.2] text-[#0a1f5c] font-light"
            >
              "MKA Business Solutions reframed the way we think about growth.
              Six months later our pipeline is unrecognizable — and our team is
              finally aligned."
            </blockquote>
            <footer className="mt-8 font-fraunces text-[#b8964f] tracking-widest text-sm uppercase">
              — Chief Operating Officer · Regional Healthcare Group
            </footer>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#e6dbc7] bg-[#f0e8d8]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 text-center">
          <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
            IV. Begin
          </div>
          <h3 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#0a1f5c] leading-tight max-w-3xl mx-auto">
            Ready to build something{" "}
            <em className="italic text-[#b8964f]">worth remembering?</em>
          </h3>
          <Link
            to="/contact"
            data-testid="luxe-home-final-cta"
            className="mt-10 inline-flex items-center gap-3 bg-[#0a1f5c] text-[#f7f2ea] px-10 py-5 rounded-full font-fraunces text-base hover:bg-[#b8964f] hover:text-[#0a1f5c] transition-all"
          >
            Request a Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
